import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react'
import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GenericField } from '../../../../rapid-ui'

interface DescriptionComboboxProps {
	label: ReactNode
	value: string
	onChange: (value: string) => void
	options: string[]
	placeholder?: string
}

/**
 * Freeform combobox: pick one of the preset `options` or type a custom value.
 * The typed text is committed as you type, so any value is allowed.
 */
export function DescriptionCombobox({ label, value, onChange, options, placeholder }: DescriptionComboboxProps) {
	const { t } = useTranslation('common')
	const [query, setQuery] = useState('')
	const trimmedQuery = query.trim()
	const filtered = trimmedQuery === ''
		? options
		: options.filter((option) => option.toLowerCase().includes(trimmedQuery.toLowerCase()))
	const showCustomOption = trimmedQuery !== ''
		&& !options.some((option) => option.toLowerCase() === trimmedQuery.toLowerCase())

	return (
		<GenericField label={label}>
			<div className="has-data-open:ring-4 ring-blue-100 rounded-md w-full relative">
				<Combobox
					immediate
					value={value}
					onChange={(next: string | null) => onChange(next ?? '')}
					onClose={() => setQuery('')}
				>
					<div className="relative flex items-center rounded-md ring-1 ring-grey-100 bg-white">
						<ComboboxInput
							className="flex-1 py-2 pl-3 pr-10 outline-none bg-transparent text-grey-800 text-base min-w-0 rounded-md"
							displayValue={(current: string) => current}
							onChange={(event) => {
								setQuery(event.target.value)
								onChange(event.target.value)
							}}
							placeholder={placeholder}
						/>
						<ComboboxButton className="absolute inset-y-0 right-0 flex items-center px-3 cursor-pointer text-grey-500">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 10 10"
								height={10}
								width={10}
								className="group-data-[open]:rotate-180 transition-transform duration-300"
							>
								<path
									fill="currentColor"
									d="M5 7.874a1.238 1.238 0 0 1 -0.879 -0.364L0.206 3.393A0.75 0.75 0 0 1 1.294 2.36l3.525 3.712a0.249 0.249 0 0 0 0.362 0L8.706 2.36a0.75 0.75 0 1 1 1.088 1.033L5.9 7.5a1.27 1.27 0 0 1 -0.9 0.374Z"
									strokeWidth={1}
								/>
							</svg>
						</ComboboxButton>
					</div>
					<ComboboxOptions
						anchor="bottom"
						transition
						className="w-[var(--input-width)] [--anchor-gap:8px] ring-1 py-1 bg-white ring-grey-100 shadow-md rounded origin-top transition duration-200 ease-out data-closed:scale-95 data-closed:opacity-0 empty:invisible"
					>
						{filtered.map((option) => (
							<ComboboxOption
								key={option}
								value={option}
								className="text-sm rounded mx-1 px-2 py-2 bg-white data-[focus]:bg-blue-100 data-[focus]:text-blue-700 cursor-pointer"
							>
								{option}
							</ComboboxOption>
						))}
						{showCustomOption && (
							<ComboboxOption
								value={trimmedQuery}
								className="text-sm rounded mx-1 px-2 py-2 bg-white text-grey-700 data-[focus]:bg-blue-100 data-[focus]:text-blue-700 cursor-pointer"
							>
								{t('inventory.useCustomType', { value: trimmedQuery })}
							</ComboboxOption>
						)}
					</ComboboxOptions>
				</Combobox>
			</div>
		</GenericField>
	)
}
